import React, { useState } from "react";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { FullProductApiModel } from "lib/@types/service/product/api/product.api.model";

import { getFeaturesGroupedPerKind, getSortedFeatureKinds } from "../helpers";
import { InfoBlockHeading } from "../infoBlockHeading/infoBlockHeading.component";
import { FavvCode } from "./favvCode/favvCode.component";
import { FeatureLabels } from "./featureLabels/featureLabels.component";
import { FeatureSection } from "./featureSection/featureSection.component";
import { SafetyDocuments } from "./safetyDocuments/safetyDocuments.component";

type FeatureListProps = {
    product: FullProductApiModel;
    maxVisibleFeatures: number;
};

const FAVVCODE_OTHERS = "ANDERE";

const FeatureList = ({ product, maxVisibleFeatures }: FeatureListProps) => {
    const { t } = useGranite();
    const [showMobileMore, setShowMobileMore] = useState(false);

    const sortedFeatureKinds = getSortedFeatureKinds(product, t);
    const featuresGroupedPerKind = getFeaturesGroupedPerKind(product, t);

    const displayFavvCodeBlock =
        product.favvCode != null && !product.favvCode.includes(FAVVCODE_OTHERS);

    const isReadMoreDataAvailable =
        product.precautionaryStatements.length > 0 ||
        product.sdsUrl ||
        product.dopUrl;

    const safetyInfoAvailable =
        !!product.signalword ||
        product.safetyIcons.length > 0 ||
        product.hazardStatements.length > 0 ||
        isReadMoreDataAvailable;

    return (
        <section
            className={`info-block js-info-block js-anchor-scroll-spy__block ${
                showMobileMore ? "info-block--active" : ""
            }`}
        >
            <InfoBlockHeading
                title={t("productDetail.anchor.title.features")}
                onClick={() => setShowMobileMore(state => !state)}
            />
            <section className="info-block__content">
                <div className="row">
                    <div className="col-12">
                        <span
                            id={t("productDetail.anchor.hash.features")}
                            className="anchor-span"
                        ></span>
                        <h4 className="title mb-3 font-weight-bold anchor--pdp-info-nav">
                            <span
                                className="title__content"
                                data-test={`titleComponent-${t(
                                    "productDetail.anchor.title.features",
                                )}`}
                            >
                                {t("productDetail.anchor.title.features")}
                            </span>
                        </h4>

                        {sortedFeatureKinds.map(featureKind => {
                            return (
                                <FeatureSection
                                    key={`featureKind-${featureKind.code}`}
                                    featureKind={featureKind}
                                    maxVisibleFeatures={maxVisibleFeatures}
                                    featuresGroupedPerKind={
                                        featuresGroupedPerKind
                                    }
                                />
                            );
                        })}
                        {displayFavvCodeBlock && (
                            <FavvCode code={product.favvCode} />
                        )}
                        {safetyInfoAvailable && (
                            <SafetyDocuments
                                signalWord={product.signalword}
                                hazardStatements={product.hazardStatements.join(
                                    " ",
                                )}
                                dopUrl={product.dopUrl}
                                sdsUrl={product.sdsUrl}
                                precautionaryStatements={product.precautionaryStatements.join(
                                    " ",
                                )}
                                safetyIcons={product.safetyIcons}
                                isReadMoreDataAvailable={
                                    isReadMoreDataAvailable
                                }
                            />
                        )}
                        <FeatureLabels product={product} />
                    </div>
                </div>
            </section>
        </section>
    );
};

export { FeatureList };
